import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import TextField from '@/components/atoms/TextField';
import type { AuditLogFilters } from '@/domain/auditLog';

import {
    type AuditDraftKey,
    appliedText,
    draftInvalidMessage,
    draftMaxLength,
    draftOf,
    isDraftApplied,
    NUMERIC_DRAFT_KEYS,
    readDraft,
    withDraft,
} from './auditLogFilterDrafts';
import type { AuditTypingStep } from './useAuditLogFilterState';

import styles from './FilterTextField.module.scss';

const DEBOUNCE_MS = 300;

interface FilterTextFieldProps {
    field: AuditDraftKey;
    label: string;
    filters: AuditLogFilters;
    onType: (next: AuditLogFilters, step: AuditTypingStep) => void;
    placeholder?: string;
    /**
     * Read with the input and shown on hover, so the row keeps one line. A disabled field
     * also shows it in place of its placeholder, as the reason it waits.
     */
    hint?: string;
    disabled?: boolean;
    /** The toolbar puts the label inside the field's box to keep to one line. */
    inline?: boolean;
}

/** The ids of the texts shown under a field, for `aria-describedby`. */
function describedBy(...ids: (string | undefined)[]): string | undefined {
    const shown = ids.filter((id): id is string => id !== undefined);
    return shown.length > 0 ? shown.join(' ') : undefined;
}

/**
 * A typed filter. It reports once the typing pauses, so each keystroke does not refetch,
 * and at once on blur, so clicking Export straight after typing exports what was typed:
 * blur runs on mousedown, and React renders the new URL before the click. Text that cannot
 * filter is marked as ignored rather than sent, since the backend would answer it with a
 * 400 that the page can only show as a generic load error. Ids use a text input: a number
 * input empties itself for text such as "abc", so it could not say what it ignores.
 */
export default function FilterTextField(props: FilterTextFieldProps) {
    const { field, label, filters, onType, placeholder, hint, disabled, inline = false } = props;
    const { t } = useTranslation();
    const id = useId();
    const applied = draftOf(field, filters);
    const [draft, setDraft] = useState(applied);
    // The applied text this field expects to see after its own reports, so it can tell
    // them apart from a change made elsewhere: a click, a chip, "Clear all" or Back.
    const expected = useRef(applied);
    const typedSinceFocus = useRef(false);
    const latest = useRef({ filters, onType });
    const timer = useRef<number | undefined>(undefined);

    useEffect(() => {
        latest.current = { filters, onType };
    }, [filters, onType]);

    useEffect(() => {
        if (applied === expected.current) return;
        expected.current = applied;
        setDraft(applied);
    }, [applied]);

    useEffect(() => {
        if (isDraftApplied(field, draft, applied)) return;
        timer.current = window.setTimeout(() => {
            expected.current = appliedText(field, draft);
            typedSinceFocus.current = true;
            latest.current.onType(withDraft(latest.current.filters, field, draft), 'type');
        }, DEBOUNCE_MS);
        return () => window.clearTimeout(timer.current);
    }, [field, draft, applied]);

    function handleBlur() {
        window.clearTimeout(timer.current);
        const changed = !isDraftApplied(field, draft, applied);
        if (!changed && !typedSinceFocus.current) return;
        typedSinceFocus.current = false;
        expected.current = appliedText(field, draft);
        onType(withDraft(filters, field, draft), 'commit');
    }

    const invalid = !disabled && readDraft(field, draft).invalid;
    const hintId = `${id}-hint`;
    const invalidId = `${id}-invalid`;

    return (
        <div className={classNames(styles.field, { [styles.inline]: inline })} title={hint}>
            <label className={styles.label} htmlFor={id}>
                {label}
            </label>
            <TextField
                id={id}
                className={styles.input}
                type="text"
                inputMode={NUMERIC_DRAFT_KEYS.includes(field) ? 'numeric' : undefined}
                maxLength={draftMaxLength(field)}
                placeholder={disabled && hint !== undefined ? hint : placeholder}
                disabled={disabled}
                invalid={invalid}
                aria-describedby={describedBy(
                    invalid ? invalidId : undefined,
                    hint === undefined ? undefined : hintId
                )}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onBlur={handleBlur}
            />
            {invalid && (
                <span id={invalidId} className={styles.feedback}>
                    {t(draftInvalidMessage(field))}
                </span>
            )}
            {hint !== undefined && (
                // A disabled field shows its hint as the placeholder; an enabled one needs it
                // on screen, since it explains how the value matches.
                <span id={hintId} className={disabled ? styles.visuallyHidden : styles.note}>
                    {hint}
                </span>
            )}
        </div>
    );
}
